import React from 'react';
import './App.css';

function App() {
  return (
    <div className="App">
      <header className="header">
        <div className="container">
          <div className="header-content">
            <div className="logo">
              <span className="logo-icon">🍌</span>
              <h1>Banana Studio AI</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="main">
        <div className="container">
          <div className="privacy-policy">
            <h1 className="main-title">Privacy Policy</h1>
            <p className="main-subtitle">
              Last updated: {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>

            <div className="highlight">
              <strong>Quick Summary:</strong> We respect your privacy and are committed to protecting your personal information. 
              We only collect data necessary to provide our AI image generation service and never share your photos or personal data with third parties.
            </div>

            <div className="section">
              <h2>1. Information We Collect</h2>
              
              <h3>1.1 Personal Information</h3>
              <p>When you use our service, we may collect:</p>
              <ul>
                <li><strong>Google Account Information:</strong> When you sign in with Google, we collect your email address, name, and profile picture</li>
                <li><strong>Photos:</strong> Images you upload for AI processing (temporarily stored during generation)</li>
                <li><strong>Usage Data:</strong> Information about how you use our app, including generation history and preferences</li>
                <li><strong>Device Information:</strong> Device type, operating system, and app version for technical support</li>
              </ul>

              <h3>1.2 Automatically Collected Information</h3>
              <ul>
                <li>IP address and location data (for security and service optimization)</li>
                <li>App usage statistics and crash reports</li>
                <li>Cookies and similar tracking technologies</li>
              </ul>
            </div>

            <div className="section">
              <h2>2. How We Use Your Information</h2>
              
              <h3>2.1 Service Provision</h3>
              <ul>
                <li>Process your photos to generate amazing action figures using AI technology</li>
                <li>Maintain your account and generation history</li>
                <li>Provide customer support and technical assistance</li>
                <li>Process payments and manage credits</li>
              </ul>

              <h3>2.2 Service Improvement</h3>
              <ul>
                <li>Analyze usage patterns to improve our AI models</li>
                <li>Develop new features and styles</li>
                <li>Optimize app performance and user experience</li>
                <li>Conduct research and analytics (anonymized data only)</li>
              </ul>

              <h3>2.3 Communication</h3>
              <ul>
                <li>Send important service updates and notifications</li>
                <li>Respond to your inquiries and support requests</li>
                <li>Provide information about new features and promotions (with your consent)</li>
              </ul>
            </div>

            <div className="section">
              <h2>3. Information Sharing and Disclosure</h2>
              
              <h3>3.1 We Do NOT Share Your Personal Data</h3>
              <p>We do not sell, trade, or rent your personal information to third parties. Your photos and personal data remain private and secure.</p>

              <h3>3.2 Limited Sharing Scenarios</h3>
              <p>We may share information only in these limited circumstances:</p>
              <ul>
                <li><strong>Service Providers:</strong> Trusted third-party services that help us operate our app (e.g., cloud storage, payment processing)</li>
                <li><strong>Legal Requirements:</strong> When required by law or to protect our rights and safety</li>
                <li><strong>Business Transfers:</strong> In case of merger or acquisition (with notice to users)</li>
                <li><strong>Consent:</strong> When you explicitly give us permission to share specific information</li>
              </ul>

              <h3>3.3 AI Processing</h3>
              <p>Your photos are processed by our AI systems to generate action figures. This processing:</p>
              <ul>
                <li>Occurs on secure servers</li>
                <li>Is temporary (photos are deleted after generation)</li>
                <li>Does not involve human review of your images</li>
                <li>Uses industry-standard encryption</li>
              </ul>
            </div>

            <div className="section">
              <h2>4. Data Security</h2>
              
              <h3>4.1 Security Measures</h3>
              <ul>
                <li><strong>Encryption:</strong> All data is encrypted in transit and at rest</li>
                <li><strong>Access Controls:</strong> Strict access controls and authentication</li>
                <li><strong>Regular Audits:</strong> Security audits and vulnerability assessments</li>
                <li><strong>Secure Infrastructure:</strong> Industry-standard cloud security</li>
              </ul>

              <h3>4.2 Data Retention</h3>
              <ul>
                <li><strong>Photos:</strong> Deleted immediately after AI processing (within 24 hours)</li>
                <li><strong>Account Data:</strong> Retained while your account is active</li>
                <li><strong>Usage Data:</strong> Anonymized and retained for up to 2 years</li>
                <li><strong>Support Records:</strong> Retained for 1 year for customer service</li>
              </ul>
            </div>

            <div className="section">
              <h2>5. Your Rights and Choices</h2>
              
              <h3>5.1 Access and Control</h3>
              <ul>
                <li><strong>View Your Data:</strong> Access your account information and generation history</li>
                <li><strong>Update Information:</strong> Modify your profile and preferences</li>
                <li><strong>Delete Account:</strong> Request account deletion and data removal</li>
                <li><strong>Data Export:</strong> Download your data in a portable format</li>
              </ul>

              <h3>5.2 Communication Preferences</h3>
              <ul>
                <li>Opt out of promotional emails</li>
                <li>Control notification settings in the app</li>
                <li>Unsubscribe from marketing communications</li>
              </ul>

              <h3>5.3 GDPR Rights (EU Users)</h3>
              <ul>
                <li>Right to access your personal data</li>
                <li>Right to rectification of inaccurate data</li>
                <li>Right to erasure ("right to be forgotten")</li>
                <li>Right to restrict processing</li>
                <li>Right to data portability</li>
                <li>Right to object to processing</li>
              </ul>
            </div>

            <div className="section">
              <h2>6. Children's Privacy</h2>
              <p>Our service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.</p>
              
              <p>For users between 13-18 years old, we recommend parental guidance when using our service.</p>
            </div>

            <div className="section">
              <h2>7. International Data Transfers</h2>
              <p>Your information may be transferred to and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your data during international transfers, including:</p>
              <ul>
                <li>Standard Contractual Clauses (SCCs)</li>
                <li>Adequacy decisions by relevant authorities</li>
                <li>Certification schemes and codes of conduct</li>
              </ul>
            </div>

            <div className="section">
              <h2>8. Cookies and Tracking</h2>
              
              <h3>8.1 Types of Cookies</h3>
              <ul>
                <li><strong>Essential Cookies:</strong> Required for app functionality</li>
                <li><strong>Analytics Cookies:</strong> Help us understand app usage</li>
                <li><strong>Preference Cookies:</strong> Remember your settings</li>
              </ul>

              <h3>8.2 Cookie Management</h3>
              <p>You can control cookies through your browser settings. However, disabling certain cookies may affect app functionality.</p>
            </div>

            <div className="section">
              <h2>9. Third-Party Services</h2>
              <p>Our app integrates with the following third-party services:</p>
              <ul>
                <li><strong>Google Sign-In:</strong> For authentication (subject to Google's privacy policy)</li>
                <li><strong>Firebase:</strong> For data storage and analytics (subject to Google's privacy policy)</li>
                <li><strong>Replicate API:</strong> For AI image processing (subject to Replicate's privacy policy)</li>
                <li><strong>Payment Processors:</strong> For credit purchases (subject to their respective privacy policies)</li>
              </ul>
            </div>

            <div className="section">
              <h2>10. Changes to This Privacy Policy</h2>
              <p>We may update this Privacy Policy from time to time. We will notify you of any changes by:</p>
              <ul>
                <li>Posting the new Privacy Policy on this page</li>
                <li>Sending you an email notification</li>
                <li>Showing a notice in the app</li>
              </ul>
              <p>Your continued use of our service after changes become effective constitutes acceptance of the updated policy.</p>
            </div>

            <div className="contact-info">
              <h2>11. Contact Us</h2>
              <p>If you have any questions about this Privacy Policy or our data practices, please contact us:</p>
              <ul>
                <li><strong>Email:</strong> privacy@nanobanana.ai</li>
                <li><strong>Support:</strong> iamjessica77777@gmail.com</li>
                <li><strong>Data Protection Officer:</strong> dpo@nanobanana.ai</li>
              </ul>
              <p>We will respond to your inquiry within 30 days.</p>
            </div>

            <div className="last-updated">
              <p>This Privacy Policy was last updated on {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</p>
              <p>Version 1.0</p>
            </div>
          </div>
        </div>
      </main>

      <footer className="footer">
        <div className="container">
          <p>&copy; 2024 jessicaLabs. All rights reserved.</p>
          <p>Protecting your privacy is our priority.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
